/**
 * Карточка заявки на отсутствие с маршрутом согласования.
 *
 * Открывается кликом по отсутствию в любом месте (Отсутствия команды, Мои
 * отсутствия, На согласовании, История). Показывает тип/период/комментарии и
 * степпер маршрута: Подал → ЛР [ФИО] → HR → Согласовано, с подсветкой текущего
 * шага и «что дальше». Тянет деталь через /api/absences/<id>/ (поле
 * `approval_route`). Доступ к детали есть у владельца, ЛР/HR и руководителя из
 * dashboard-scope — то есть у всех, кто видит заявку в списке.
 */

import { useEffect, useState } from "react";
import { Alert, Badge, Group, Loader, Modal, Stack, Text, Timeline } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { Check, Clock, Dot, RotateCcw } from "lucide-react";
import dayjs from "dayjs";

import { fetchAbsence, type Absence, type AbsenceRouteStep } from "../api/client";

const STATE_META: Record<
  AbsenceRouteStep["state"],
  { color: string; icon: typeof Check; note: string }
> = {
  done: { color: "teal", icon: Check, note: "" },
  current: { color: "blue", icon: Clock, note: "сейчас здесь" },
  pending: { color: "gray", icon: Dot, note: "ожидает" },
  returned: { color: "orange", icon: RotateCcw, note: "возврат" },
};

interface Props {
  absenceId: number | null;
  opened: boolean;
  onClose: () => void;
}

export function AbsenceRouteModal({ absenceId, opened, onClose }: Props) {
  const [absence, setAbsence] = useState<Absence | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!opened || absenceId == null) return;
    setLoading(true);
    setError(null);
    setAbsence(null);
    fetchAbsence(absenceId)
      .then(setAbsence)
      .catch((e: Error) => setError(e.message || "Не удалось загрузить заявку."))
      .finally(() => setLoading(false));
  }, [opened, absenceId]);

  const isMobile = useMediaQuery("(max-width: 48em)");
  const route = absence?.approval_route ?? [];
  const currentIdx = route.findIndex((s) => s.state === "current");
  const doneCount = route.filter((s) => s.state === "done").length;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Заявка на отсутствие"
      size="md"
      fullScreen={isMobile}
    >
      {loading && (
        <Group justify="center" py="md">
          <Loader size="sm" />
        </Group>
      )}
      {error && <Alert color="red">{error}</Alert>}
      {absence && (
        <Stack gap="md" data-testid="absence-route-modal">
          <Group gap="xs">
            <Badge color="indigo" variant="light">
              {absence.absence_type_display}
            </Badge>
            <Text size="sm" fw={500}>
              {dayjs(absence.start_date).format("DD.MM.YYYY")} –{" "}
              {dayjs(absence.end_date).format("DD.MM.YYYY")}
            </Text>
          </Group>
          <Text size="xs" c="dimmed">
            {absence.employee.full_name} · дней раб. {absence.working_days} / кал.{" "}
            {absence.calendar_days ?? "—"}
          </Text>
          {absence.comment && <Text size="sm">Комментарий: {absence.comment}</Text>}

          <div>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={8}>
              Маршрут согласования
            </Text>
            <Timeline
              active={currentIdx >= 0 ? currentIdx : doneCount - 1}
              bulletSize={22}
              lineWidth={2}
            >
              {route.map((s) => {
                const meta = STATE_META[s.state];
                const Icon = meta.icon;
                return (
                  <Timeline.Item
                    key={s.key}
                    color={meta.color}
                    bullet={<Icon size={12} />}
                    title={
                      <Group gap={6}>
                        <Text size="sm" fw={s.state === "current" ? 600 : 400}>
                          {s.label}
                        </Text>
                        {meta.note && (
                          <Badge size="xs" color={meta.color} variant="light">
                            {meta.note}
                          </Badge>
                        )}
                      </Group>
                    }
                  >
                    {s.actor && (
                      <Text size="xs" c="dimmed">
                        {s.actor}
                      </Text>
                    )}
                  </Timeline.Item>
                );
              })}
            </Timeline>
          </div>

          {absence.pm_comment && (
            <Alert color="orange" title="Комментарий руководителя проекта">
              {absence.pm_comment}
            </Alert>
          )}
          {absence.lr_comment && (
            <Alert color="orange" title="Комментарий ЛР">
              {absence.lr_comment}
            </Alert>
          )}
          {absence.hr_comment && (
            <Alert color="orange" title="Комментарий HR">
              {absence.hr_comment}
            </Alert>
          )}
        </Stack>
      )}
    </Modal>
  );
}
